import { Suspense, lazy } from "react";
import { FormProvider } from "react-hook-form";
import { Toaster } from "react-hot-toast";
import { getTrainingInputSchema } from "../../../schema";
import type {
  AccessToken,
  PlaygroundPermissions,
  Prediction,
  Version,
} from "../../../types";
import {
  PlaygroundContext,
  type PlaygroundContextValue,
} from "../../api-playground/context";
import { useCreateTrainingForm, type TrainingAction } from "../shared";

const TrainingForm = lazy(() => import("../training-form"));

export default function GenericTraining({
  authenticated,
  avatar,
  destinations,
  modelInputSettings,
  owner,
  permissions,
  requiresCredit,
  token,
  training,
  trainingVersion,
  version,
}: {
  authenticated: boolean;
  avatar: string;
  destinations?: { name: string; username: string }[];
  modelInputSettings?: { hidden: string[] };
  owner: string;
  permissions?: PlaygroundPermissions;
  requiresCredit: boolean;
  token: AccessToken | null;
  training?: Prediction;
  trainingVersion?: Version;
  version: Version;
}) {
  const trainingInputSchema = getTrainingInputSchema(version._extras);

  const action: TrainingAction =
    training && trainingVersion ? "update" : "create";

  const form = useCreateTrainingForm({
    action,
    training,
    trainingVersion,
    version,
    destinations,
  });

  const context: PlaygroundContextValue = {
    elementVisibility: {
      tweakButton: false,
    },
    features: {},
    hideAdvancedInputs: false,
    isAuthenticated: true,
    model: version._extras.model,
    modelInputSettings: modelInputSettings ?? { hidden: [] },
    modelStatus: null,
    permissions: permissions ?? {
      create_example: false,
      debug: false,
      delete: false,
      edit_featured_inputs: false,
      report: version._extras.model.visibility === "public",
      run: false,
      share: true,
      tweak: true,
    },
    requiresCredit,
    renderMode: "default",
    token: token ?? null,
    usesVersionlessApi: false,
    version,
  };

  return (
    <PlaygroundContext.Provider value={context}>
      <Toaster />
      <Suspense>
        <FormProvider {...form}>
          <TrainingForm
            trainingVersion={trainingVersion}
            version={version}
            authenticated={authenticated}
            owner={owner}
            avatar={avatar}
            token={token}
            destinations={destinations}
            action={action}
            properties={trainingInputSchema?.properties || {}}
          />
        </FormProvider>
      </Suspense>
    </PlaygroundContext.Provider>
  );
}
